import { TImportCustomerUserRow } from "@/api/services/main/customer-user/dtos/customer-user.dto";
import { INVALID_IMPORT_PRODUCT_NOT_EMPTY } from "@/constants/invalids";
import { USER_ROLE_LABEL } from "@/types/enum";
import type { TUserRole } from "@/types/enum";
import { convertFullWidthToHalfWidth } from "@/utils/pieces";
import { replaceStr } from "@/utils/string";
import Encoding from "encoding-japanese";
import Papa from "papaparse";

export type TParsedImportRow = {
  rowNumber: number;
  fullName: string;
  email: string;
  roleLabel: string;
  productLabel: string;
};

const HEADER_KEYS = {
  fullName: "ユーザー名",
  email: "メールアドレス",
  role: "権限",
  product: "プロダクトアクセス権限",
} as const;

const REQUIRED_HEADERS = [HEADER_KEYS.fullName, HEADER_KEYS.email, HEADER_KEYS.role, HEADER_KEYS.product] as const;

export class ImportParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ImportParseError";
    Object.setPrototypeOf(this, ImportParseError.prototype);
  }
}

const assertRequiredHeaders = (headers: string[]): void => {
  const normalized = headers.map((header) => header.trim());
  const missing = REQUIRED_HEADERS.filter((required) => !normalized.includes(required));
  if (missing.length > 0) {
    throw new ImportParseError(`ファイルの形式が正しくありません。必須の列が見つかりません: ${missing.join("、")}`);
  }
};

const ROLE_LABEL_TO_VALUE: Record<string, TUserRole> = Object.entries(USER_ROLE_LABEL).reduce(
  (acc, [value, label]) => ({ ...acc, [label]: value as TUserRole }),
  {} as Record<string, TUserRole>,
);

export const mapRoleLabelToValue = (label: string): TUserRole | null => ROLE_LABEL_TO_VALUE[label.trim()] ?? null;

const toParsedRows = (records: Record<string, string>[]): TParsedImportRow[] =>
  records.map((record, index) => ({
    rowNumber: index + 1,
    fullName: (record[HEADER_KEYS.fullName] ?? "").trim(),
    email: convertFullWidthToHalfWidth((record[HEADER_KEYS.email] ?? "").trim()),
    roleLabel: (record[HEADER_KEYS.role] ?? "").trim(),
    // 区切り文字（「,」「、」）の分解と productName との完全一致判定はサーバー側で行うため、表記を加工せず渡す。
    productLabel: (record[HEADER_KEYS.product] ?? "").trim(),
  }));

const parseCsv = async (file: File): Promise<TParsedImportRow[]> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const detected = Encoding.detect(bytes);
  const text = Encoding.convert(bytes, { to: "UNICODE", from: detected || "AUTO", type: "string" }) as string;
  const result = Papa.parse<Record<string, string>>(text, { header: true, skipEmptyLines: true });
  assertRequiredHeaders(result.meta.fields ?? []);
  return toParsedRows(result.data);
};

const parseXlsx = async (file: File): Promise<TParsedImportRow[]> => {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const worksheet = workbook.worksheets[0];
  if (!worksheet) return [];

  const headerRow = worksheet.getRow(1);
  const headerByColumn: Record<number, string> = {};
  headerRow.eachCell((cell, colNumber) => {
    headerByColumn[colNumber] = String(cell.value ?? "").trim();
  });
  assertRequiredHeaders(Object.values(headerByColumn));

  const records: Record<string, string>[] = [];
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    const record: Record<string, string> = {};
    row.eachCell((cell, colNumber) => {
      const key = headerByColumn[colNumber];
      if (key) record[key] = String(cell.value ?? "").trim();
    });
    const hasValue = Object.values(record).some((value) => value !== "");
    if (hasValue) records.push(record);
  });

  return toParsedRows(records);
};

export const parseImportFile = async (file: File): Promise<TParsedImportRow[]> => {
  const lowerName = file.name.toLowerCase();
  const isExcel = lowerName.endsWith(".xlsx") || lowerName.endsWith(".xlsm");
  return isExcel ? parseXlsx(file) : parseCsv(file);
};

/**
 * Every imported user must land on at least one product, mirroring the create/edit form. The API
 * only rejects unknown product names, so the empty case is caught here before the validate call.
 */
export const collectMissingProductErrors = (rows: TParsedImportRow[]): string[] =>
  rows.filter((row) => !row.productLabel).map((row) => replaceStr(INVALID_IMPORT_PRODUCT_NOT_EMPTY, { row: String(row.rowNumber) }));

export const toImportRequestRow = (row: TParsedImportRow): TImportCustomerUserRow => ({
  fullName: row.fullName,
  email: row.email,
  role: mapRoleLabelToValue(row.roleLabel) ?? row.roleLabel,
  products: row.productLabel,
});
